import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Filter, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { ProductImageCarousel } from '../components/ProductImageCarousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Shop = ({ addToCart }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products
      .filter(p => selectedCategory === 'all' || p.category === selectedCategory)
      .filter(p => !q || (p.name || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q));
  }, [products, selectedCategory, query]);

  const categories = [
    { value: 'all', label: 'All Products' },
    { value: 'furniture', label: 'Furniture' },
    { value: 'decor', label: 'Decor' }
  ];

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart!`, {
      duration: 2000,
      style: { backgroundColor: '#00CED1', color: 'white' }
    });
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Furniture & <span style={{ color: '#00CED1' }}>Decor Shop</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Discover our curated collection of premium furniture and decor pieces for your modern space
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 px-4 bg-white border-b">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <Input
                type="search"
                placeholder="Search products…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10"
                data-testid="shop-search-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5" style={{ color: '#00CED1' }} />
              <span className="font-semibold text-gray-700">Filter by:</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <Button
                  key={category.value}
                  variant={selectedCategory === category.value ? 'default' : 'outline'}
                  onClick={() => setSelectedCategory(category.value)}
                  className={`font-semibold transition-all duration-300 ${
                    selectedCategory === category.value 
                      ? 'text-white' 
                      : 'hover:scale-105'
                  }`}
                  style={
                    selectedCategory === category.value 
                      ? { backgroundColor: '#00CED1' }
                      : { borderColor: '#00CED1', color: '#00CED1' }
                  }
                >
                  {category.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-gray-600">Loading products...</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product) => (
              <Card 
                key={product.id}
                className="group overflow-hidden border-0 hover:shadow-2xl transition-all duration-500"
              >
                <div className="relative h-80 overflow-hidden bg-gray-100">
                  <ProductImageCarousel product={product} className="h-80" />
                  {product.featured && (
                    <Badge 
                      className="absolute top-4 left-4 text-white z-30"
                      style={{ backgroundColor: '#FF0000' }}
                    >
                      Featured
                    </Badge>
                  )}
                  {!product.inStock && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-30 pointer-events-none">
                      <Badge className="text-white bg-gray-800">
                        Out of Stock
                      </Badge>
                    </div>
                  )}
                </div>
                <CardContent className="p-6">
                  <div className="mb-4">
                    <Badge 
                      variant="outline" 
                      className="mb-3 capitalize"
                      style={{ borderColor: '#00CED1', color: '#00CED1' }}
                    >
                      {product.category}
                    </Badge>
                    <Link
                      to={`/shop/${product.id}`}
                      className="block hover:underline"
                      data-testid={`shop-name-link-${product.id}`}
                    >
                      <h3 className="text-xl font-bold mb-2">{product.name}</h3>
                    </Link>
                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                      {product.description}
                    </p>
                    <Link
                      to={`/shop/${product.id}`}
                      className="inline-block mt-2 text-xs font-medium hover:underline"
                      style={{ color: '#00CED1' }}
                      data-testid={`shop-view-details-${product.id}`}
                    >
                      View details →
                    </Link>
                  </div>
                  
                  <div className="flex justify-between items-center pt-4 border-t">
                    <span className="text-3xl font-bold" style={{ color: '#00CED1' }}>
                      R{product.price.toFixed(2)}
                    </span>
                    <Button
                      onClick={() => handleAddToCart(product)}
                      disabled={!product.inStock}
                      className="text-white font-semibold hover:scale-105 transition-transform duration-300"
                      style={{ backgroundColor: product.inStock ? '#00CED1' : '#gray' }}
                      data-testid={`shop-add-to-cart-${product.id}`}
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Add to Cart
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          )}

          {!loading && filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-gray-500">No products found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <div className="text-4xl mb-4">🚚</div>
              <h3 className="text-xl font-bold mb-2">Free Shipping</h3>
              <p className="text-gray-600">On orders over R10,000</p>
            </div>
            <div className="p-6">
              <div className="text-4xl mb-4">🔒</div>
              <h3 className="text-xl font-bold mb-2">Secure Payment</h3>
              <p className="text-gray-600">100% secure transactions</p>
            </div>
            <div className="p-6">
              <div className="text-4xl mb-4">↩️</div>
              <h3 className="text-xl font-bold mb-2">Easy Returns</h3>
              <p className="text-gray-600">30-day return policy</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Shop;
